// ABOUTME: Image cell renderer for PIM data tables: thumbnails that open full size when clicked.
// ABOUTME: Media ids resolve through the media endpoint, external URLs are used as they are.
import {
    Box,
    Dialog,
    Img,
    InsertDriveFileOutlinedIcon,
    Stack,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import type { CustomCellRendererProps } from '@ds/data-table';
import { NodeContentValueO } from '@src/__generated__/types';
import { resolveImageThumbnailUri, resolveImageUri } from '@src/_new/utils/resolveImageUri';
import { useState } from 'react';

const THUMBNAIL_HEIGHT = 40;
const THUMBNAIL_MAX_WIDTH = 72;

export default function GridCellImageRender(props: CustomCellRendererProps) {
    const { value, disabled } = props;

    // A `media` column reaches this renderer too, and a media is not always an image: a PDF has
    // no preview to serve, so its <img> fails. Those fall back to a named placeholder instead of
    // a broken-image icon.
    const [withoutPreview, setWithoutPreview] = useState<string[]>([]);
    const [enlarged, setEnlarged] = useState<string | null>(null);

    const first = (value as NodeContentValueO[])?.[0];
    const uris = (first?.value ?? []).filter(
        (uri): uri is string => typeof uri === 'string' && uri.length > 0,
    );

    if (uris.length === 0) {
        return null;
    }

    return (
        <Box display="flex" gap={0.5} alignItems="center" opacity={disabled ? 0.5 : 1}>
            {uris.map(uri =>
                withoutPreview.includes(uri) ? (
                    <Tooltip key={uri} title={uri}>
                        <InsertDriveFileOutlinedIcon color="action" fontSize="small" />
                    </Tooltip>
                ) : (
                    // A button, not an image with a click handler: the preview is an action, so
                    // it has to be reachable by keyboard like every other action in the row.
                    <Box
                        key={uri}
                        component="button"
                        aria-label={uri}
                        onClick={(event: React.MouseEvent) => {
                            // `Box` types its props on a div, so the element cannot declare
                            // `type="button"` and defaults to submit: preventDefault is what
                            // stops a click from submitting the form the grid may sit in.
                            event.preventDefault();
                            // The row has its own click; enlarging must not also open the node.
                            event.stopPropagation();
                            setEnlarged(uri);
                        }}
                        padding={0}
                        border="none"
                        bgcolor="transparent"
                        display="block"
                        cursor="pointer"
                    >
                        <Img
                            src={resolveImageThumbnailUri(uri)}
                            alt=""
                            onError={() => setWithoutPreview(failed => [...failed, uri])}
                            height={THUMBNAIL_HEIGHT}
                            maxWidth={THUMBNAIL_MAX_WIDTH}
                            objectFit="cover"
                            borderRadius={1}
                            display="block"
                        />
                    </Box>
                ),
            )}

            {enlarged && (
                <Dialog open onClose={() => setEnlarged(null)} maxWidth="lg">
                    <Stack
                        padding={1}
                        gap={1}
                        alignItems="center"
                        onClick={event => event.stopPropagation()}
                    >
                        {/* The original, not the thumbnail: enlarging a 40px preview shows
                            nothing more than the preview did. */}
                        <Img
                            src={resolveImageUri(enlarged)}
                            alt=""
                            maxWidth="100%"
                            maxHeight="80vh"
                            objectFit="contain"
                        />
                        <Typography variant="caption" color="text.secondary">
                            {enlarged}
                        </Typography>
                    </Stack>
                </Dialog>
            )}
        </Box>
    );
}
